"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/utilities/core/cn";
import { BracketTieCard } from "./BracketTieCard";
import { BracketConnector } from "./BracketConnector";
import type { BracketData, BracketRound } from "./types";
const MIN_ROUND_WIDTH = 240;
const CONNECTOR_WIDTH = 32;

/**
 * Find the best round to initially scroll to:
 * 1. First round with a live match
 * 2. First round with a scheduled (upcoming) match
 * 3. Last round (everything finished — show the Final)
 */
function findActiveRoundIndex(rounds: BracketRound[]): number {
  const liveIdx = rounds.findIndex(r => r.ties.some(t => t.matches.some(m => m.status === "live")));
  if (liveIdx !== -1) return liveIdx;
  const scheduledIdx = rounds.findIndex(r => r.ties.some(t => t.matches.some(m => m.status === "scheduled")));
  if (scheduledIdx !== -1) return scheduledIdx;
  return rounds.length - 1;
}
interface BracketGridProps {
  data: BracketData;
}
export function BracketGrid(t0) {
  const $ = _c(60);
  const {
    data
  } = t0;
  const {
    rounds,
    isOpenDraw
  } = data;
  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(rounds.length);
  const [startIndex, setStartIndex] = useState(0);
  const initializedRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== rounds.length) {
    t1 = () => {
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const observer = new ResizeObserver(t3 => {
        const [entry] = t3;
        const width = entry.contentRect.width;
        const count = Math.max(1, Math.min(Math.floor((width + CONNECTOR_WIDTH) / (MIN_ROUND_WIDTH + CONNECTOR_WIDTH)), rounds.length));
        setVisibleCount(count);
      });
      observer.observe(el);
      return () => observer.disconnect();
    };
    t2 = [rounds.length];
    $[0] = rounds.length;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== rounds || $[4] !== visibleCount) {
    t3 = () => {
      if (initializedRef.current || visibleCount >= rounds.length) {
        return;
      }
      initializedRef.current = true;
      const activeIdx = findActiveRoundIndex(rounds);
      const ideal = activeIdx - Math.floor(visibleCount / 2);
      const clamped = Math.max(0, Math.min(ideal, rounds.length - visibleCount));
      setStartIndex(clamped);
    };
    t4 = [visibleCount, rounds];
    $[3] = rounds;
    $[4] = visibleCount;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== rounds.length || $[8] !== visibleCount) {
    t5 = () => {
      setStartIndex(prev => Math.min(prev, Math.max(0, rounds.length - visibleCount)));
    };
    t6 = [visibleCount, rounds.length];
    $[7] = rounds.length;
    $[8] = visibleCount;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  const showArrows = rounds.length > visibleCount;
  const canGoLeft = startIndex > 0;
  const canGoRight = startIndex + visibleCount < rounds.length;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t7;
  let t8;
  let t9;
  if ($[11] !== canGoLeft || $[12] !== canGoRight || $[13] !== data.thirdPlace || $[14] !== isOpenDraw || $[15] !== rounds || $[16] !== showArrows || $[17] !== startIndex || $[18] !== visibleCount) {
    const visibleRounds = rounds.slice(startIndex, startIndex + visibleCount);
    const maxTies = visibleRounds[0]?.ties.length || 1;
    const showThirdPlace = data.thirdPlace && startIndex + visibleCount >= rounds.length;
    const gridCols = visibleRounds.map(_temp).join(" ");
    const gridMinHeight = maxTies * 90 + (maxTies - 1) * 8;
    let t17;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = () => setStartIndex(_temp2);
      $[29] = t17;
    } else {
      t17 = $[29];
    }
    const goLeft = t17;
    let t18;
    if ($[30] !== rounds.length || $[31] !== visibleCount) {
      t18 = () => setStartIndex(prev_1 => Math.min(rounds.length - visibleCount, prev_1 + 1));
      $[30] = rounds.length;
      $[31] = visibleCount;
      $[32] = t18;
    } else {
      t18 = $[32];
    }
    const goRight = t18;
    let t19;
    if ($[33] !== canGoLeft || $[34] !== canGoRight || $[35] !== goRight) {
      t19 = e => {
        if (e.key === "ArrowLeft" && canGoLeft) {
          e.preventDefault();
          goLeft();
        } else {
          if (e.key === "ArrowRight" && canGoRight) {
            e.preventDefault();
            goRight();
          }
        }
      };
      $[33] = canGoLeft;
      $[34] = canGoRight;
      $[35] = goRight;
      $[36] = t19;
    } else {
      t19 = $[36];
    }
    const handleKeyDown = t19;
    t10 = containerRef;
    t11 = showArrows ? 0 : undefined;
    t12 = showArrows ? handleKeyDown : undefined;
    t13 = "outline-none";
    if ($[37] !== showThirdPlace) {
      t14 = showThirdPlace ? {
        paddingBottom: 140
      } : undefined;
      $[37] = showThirdPlace;
      $[38] = t14;
    } else {
      t14 = $[38];
    }
    if ($[39] !== canGoLeft || $[40] !== canGoRight || $[41] !== goRight || $[42] !== rounds || $[43] !== showArrows || $[44] !== startIndex || $[45] !== visibleCount) {
      t15 = showArrows && <div className="flex items-center justify-center gap-3 mb-4"><button onClick={goLeft} disabled={!canGoLeft} className={cn("flex items-center justify-center w-8 h-8", "bg-background/90 backdrop-blur-sm border border-border rounded-full", "shadow-sm transition-all duration-200 cursor-pointer", canGoLeft ? "hover:bg-background hover:scale-105" : "opacity-40 cursor-default")} aria-label="Previous round"><ChevronLeft className="w-4 h-4 text-foreground" /></button><div className="flex items-center gap-1.5">{rounds.map((round, i_0) => {
            const isVisible = i_0 >= startIndex && i_0 < startIndex + visibleCount;
            return <button key={round.id} onClick={() => setStartIndex(Math.min(i_0, Math.max(0, rounds.length - visibleCount)))} className={cn("w-2 h-2 rounded-full transition-all duration-200 cursor-pointer", isVisible ? "bg-primary scale-110" : "bg-border hover:bg-muted-foreground/50")} aria-label={round.name} />;
          })}</div><button onClick={goRight} disabled={!canGoRight} className={cn("flex items-center justify-center w-8 h-8", "bg-background/90 backdrop-blur-sm border border-border rounded-full", "shadow-sm transition-all duration-200 cursor-pointer", canGoRight ? "hover:bg-background hover:scale-105" : "opacity-40 cursor-default")} aria-label="Next round"><ChevronRight className="w-4 h-4 text-foreground" /></button></div>;
      $[39] = canGoLeft;
      $[40] = canGoRight;
      $[41] = goRight;
      $[42] = rounds;
      $[43] = showArrows;
      $[44] = startIndex;
      $[45] = visibleCount;
      $[46] = t15;
    } else {
      t15 = $[46];
    }
    t16 = <div style={{
      display: "grid",
      gridTemplateColumns: gridCols
    }}>{visibleRounds.map(_temp3)}</div>;
    t7 = startIndex;
    t8 = {
      display: "grid",
      gridTemplateRows: `repeat(${maxTies}, 1fr)`,
      gridTemplateColumns: gridCols,
      rowGap: "8px",
      minHeight: `${gridMinHeight}px`,
      animation: "fadeInUp 0.3s ease-out"
    };
    t9 = visibleRounds.map((round_1, roundIndex) => {
      const roundCol = roundIndex * 2 + 1;
      const connectorCol = roundIndex * 2;
      const rowSpan = Math.max(1, Math.round(maxTies / round_1.ties.length));
      const actualRoundIndex = startIndex + roundIndex;
      return <React.Fragment key={round_1.id}>{roundIndex > 0 && (() => {
          const prevRound = rounds[actualRoundIndex - 1];
          const isBracketProgression = !isOpenDraw && prevRound && prevRound.ties.length > round_1.ties.length;
          return round_1.ties.map((__0, tieIndex) => <div key={`conn-${round_1.id}-${tieIndex}`} style={{
            gridColumn: connectorCol,
            gridRow: `${tieIndex * rowSpan + 1} / span ${rowSpan}`,
            position: "relative"
          }}>{isBracketProgression ? <BracketConnector roundIndex={actualRoundIndex} tieIndex={tieIndex} /> : <div style={{
              position: "absolute",
              left: "50%",
              top: 0,
              bottom: 0,
              borderLeft: "1.5px dashed var(--border)",
              opacity: 0.2
            }} />}</div>);
        })()}{round_1.ties.map((tie, tieIndex_0) => {
          const isLastTieOfLastRound = roundIndex === visibleRounds.length - 1 && tieIndex_0 === round_1.ties.length - 1;
          const renderThirdPlace = isLastTieOfLastRound && showThirdPlace && data.thirdPlace && data.thirdPlace.ties.length > 0;
          return <div key={`${round_1.id}-${tieIndex_0}`} style={{
            gridColumn: roundCol,
            gridRow: `${tieIndex_0 * rowSpan + 1} / span ${rowSpan}`,
            alignSelf: "center",
            position: renderThirdPlace ? "relative" : undefined
          }}><BracketTieCard tie={tie} />{renderThirdPlace && <div style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              marginTop: 24
            }}><h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground text-center mb-2">{data.thirdPlace.name}</h4>{data.thirdPlace.ties.map(_temp4)}</div>}</div>;
        })}{round_1.ties.length === 0 && <div style={{
          gridColumn: roundCol,
          gridRow: "1 / span 1",
          alignSelf: "center"
        }}><div className="flex items-center justify-center h-20 rounded-(--radius) border border-dashed border-muted-foreground/30"><span className="text-xs text-muted-foreground italic">TBD</span></div></div>}</React.Fragment>;
    });
    $[11] = canGoLeft;
    $[12] = canGoRight;
    $[13] = data.thirdPlace;
    $[14] = isOpenDraw;
    $[15] = rounds;
    $[16] = showArrows;
    $[17] = startIndex;
    $[18] = visibleCount;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t10 = $[19];
    t11 = $[20];
    t12 = $[21];
    t13 = $[22];
    t14 = $[23];
    t15 = $[24];
    t16 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  let t17;
  if ($[47] !== t7 || $[48] !== t8 || $[49] !== t9) {
    t17 = <div key={t7} style={t8}>{t9}</div>;
    $[47] = t7;
    $[48] = t8;
    $[49] = t9;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t10 || $[52] !== t11 || $[53] !== t12 || $[54] !== t13 || $[55] !== t14 || $[56] !== t15 || $[57] !== t16 || $[58] !== t17) {
    t18 = <div ref={t10} tabIndex={t11} onKeyDown={t12} className={t13} style={t14}>{t15}{t16}{t17}</div>;
    $[51] = t10;
    $[52] = t11;
    $[53] = t12;
    $[54] = t13;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  return t18;
}
function _temp4(t, i_2) {
  return <BracketTieCard key={`3rd-${i_2}`} tie={t} />;
}
function _temp3(round_0, i_1) {
  return <React.Fragment key={round_0.id}>{i_1 > 0 && <div />}<div className="text-center mb-3"><h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{round_0.name}</h4></div></React.Fragment>;
}
function _temp2(prev_0) {
  return Math.max(0, prev_0 - 1);
}
function _temp(_, i) {
  return i === 0 ? "1fr" : `${CONNECTOR_WIDTH}px 1fr`;
}